import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Star, ChevronLeft, ChevronRight, Quote } from "lucide-react";
import { TESTIMONIALS } from "@/data/testimonials";

export default function TestimonialSlider() {
  const [index, setIndex] = useState(0);
  const [dir, setDir] = useState(1);
  const count = TESTIMONIALS.length;

  const go = useCallback((next) => {
    setDir(next > index || (index === count - 1 && next === 0) ? 1 : -1);
    setIndex((next + count) % count);
  }, [index, count]);

  useEffect(() => {
    const t = setInterval(() => {
      setDir(1);
      setIndex((i) => (i + 1) % count);
    }, 6000);
    return () => clearInterval(t);
  }, [count]);

  const t = TESTIMONIALS[index];

  return (
    <div data-testid="testimonial-slider" className="relative max-w-3xl mx-auto">
      <div className="relative min-h-[300px] sm:min-h-[260px] overflow-hidden">
        <AnimatePresence mode="wait" custom={dir}>
          <motion.div
            key={index}
            custom={dir}
            initial={{ opacity: 0, x: dir * 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: dir * -40 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-3xl bg-white border border-brand-cream shadow-sm p-8 md:p-12 text-center"
          >
            <Quote size={40} className="mx-auto text-brand-orange/25 mb-4" />
            <div className="flex justify-center gap-1 mb-5">
              {Array.from({ length: t.rating }).map((_, i) => (
                <Star key={i} size={18} className="fill-brand-orange text-brand-orange" />
              ))}
            </div>
            <p className="text-lg md:text-xl leading-relaxed text-brand-dark font-medium">"{t.text}"</p>
            <div className="mt-7">
              <p className="font-extrabold text-brand-dark">{t.name}</p>
              <p className="text-sm text-brand-muted">{t.project} · {t.town}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      <div className="mt-7 flex items-center justify-center gap-4">
        <button data-testid="testimonial-prev" aria-label="Previous testimonial" onClick={() => go(index - 1)} className="grid place-items-center h-11 w-11 rounded-full border border-brand-border text-brand-dark hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors">
          <ChevronLeft size={18} />
        </button>
        <div className="flex items-center gap-2">
          {TESTIMONIALS.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to testimonial ${i + 1}`}
              data-testid={`testimonial-dot-${i}`}
              onClick={() => go(i)}
              className={`h-2 rounded-full transition-all duration-300 ${i === index ? "w-7 bg-brand-orange" : "w-2 bg-brand-border hover:bg-brand-muted"}`}
            />
          ))}
        </div>
        <button data-testid="testimonial-next" aria-label="Next testimonial" onClick={() => go(index + 1)} className="grid place-items-center h-11 w-11 rounded-full border border-brand-border text-brand-dark hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors">
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
